import baseUrl from "./baseUrl";

const INVENTORY = "client/inventory";

// --- Category ---

export function findAllCategory(): Promise<unknown[]> {
  return baseUrl
    .get<{ data: unknown[] }>(`${INVENTORY}/category/findAllCategory`)
    .then((response) => response.data.data);
}

export function findCategoryById(categoryId: string): Promise<unknown> {
  return baseUrl
    .get<{ data: unknown }>(`${INVENTORY}/category/findCategoryById`, {
      headers: { categoryid: categoryId },
    })
    .then((response) => response.data.data);
}

export type CategoryInput = { categoryName: string; releaseTypes?: string[] };

export function createCategory(data: CategoryInput): Promise<void> {
  return baseUrl
    .post(`${INVENTORY}/category/createCategory`, data)
    .then(() => undefined);
}

export function updateCategoryById(
  categoryId: string,
  data: CategoryInput
): Promise<void> {
  return baseUrl
    .patch(`${INVENTORY}/category/updateCategoryById`, data, {
      headers: { categoryid: categoryId },
    })
    .then(() => undefined);
}

/** Soft-deletes a category; its products move to the clinic's "Other". */
export function deleteCategoryById(
  categoryId: string
): Promise<{ movedProducts: number; removedSubCategories: number }> {
  return baseUrl
    .delete<{ movedProducts: number; removedSubCategories: number }>(
      `${INVENTORY}/category/deleteCategoryById`,
      { headers: { categoryid: categoryId } }
    )
    .then((response) => response.data);
}

// --- Sub Category ---

export function findAllSubCategory(): Promise<unknown[]> {
  return baseUrl
    .get<{ data: unknown[] }>(`${INVENTORY}/subCategory/findAllSubCategory`)
    .then((response) => response.data.data);
}

export function findSubCategoryById(subCategoryId: string): Promise<unknown> {
  return baseUrl
    .get<{ data: unknown }>(`${INVENTORY}/subCategory/findSubCategoryById`, {
      headers: { subcategoryid: subCategoryId },
    })
    .then((response) => response.data.data);
}

export function createSubCategory(data: {
  subCategoryName: string;
  categoryId: string;
}): Promise<void> {
  return baseUrl
    .post(`${INVENTORY}/subCategory/createSubCategory`, data)
    .then(() => undefined);
}

export function updateSubCategoryById(
  subCategoryId: string,
  data: { subCategoryName: string; categoryId: string }
): Promise<void> {
  return baseUrl
    .patch(`${INVENTORY}/subCategory/updateSubCategoryById`, data, {
      headers: { subcategoryid: subCategoryId },
    })
    .then(() => undefined);
}

/** Soft-deletes a sub category; its products keep the parent category. */
export function deleteSubCategoryById(
  subCategoryId: string
): Promise<{ movedProducts: number }> {
  return baseUrl
    .delete<{ movedProducts: number }>(
      `${INVENTORY}/subCategory/deleteSubCategoryById`,
      { headers: { subcategoryid: subCategoryId } }
    )
    .then((response) => response.data);
}

// --- Brand ---

export function findAllBrand(): Promise<unknown[]> {
  return baseUrl
    .get<{ data: unknown[] }>(`${INVENTORY}/brand/findAllBrand`)
    .then((response) => response.data.data);
}

export function findBrandById(brandId: string): Promise<unknown> {
  return baseUrl
    .get<{ data: unknown }>(`${INVENTORY}/brand/findBrandById`, {
      headers: { brandid: brandId },
    })
    .then((response) => response.data.data);
}

export function createBrand(data: { brandName: string }): Promise<void> {
  return baseUrl
    .post(`${INVENTORY}/brand/createBrand`, data)
    .then(() => undefined);
}

export function updateBrandById(
  brandId: string,
  data: { brandName: string }
): Promise<void> {
  return baseUrl
    .patch(`${INVENTORY}/brand/updateBrandById`, data, {
      headers: { brandid: brandId },
    })
    .then(() => undefined);
}

// --- Scale ---

export function findAllScale(): Promise<unknown[]> {
  return baseUrl
    .get<{ data: unknown[] }>(`${INVENTORY}/scale/findAllScale`)
    .then((response) => response.data.data);
}

export function createScale(data: {
  scale: string;
  unit: string;
}): Promise<void> {
  return baseUrl
    .post(`${INVENTORY}/scale/createScale`, data)
    .then(() => undefined);
}

// --- Product ---

export function findAllProduct(): Promise<unknown[]> {
  return baseUrl
    .get<{ data: unknown[] }>(`${INVENTORY}/product/findAllProduct`)
    .then((response) => response.data.data);
}

/** One open FIFO lot of a product, oldest first (findProductLots). */
export interface ProductLot {
  _id: string;
  receivedAt: string;
  qtyIn: number;
  qtyLeft: number;
  unitCost: number;
  value: number;
  purchaseId: string | null;
  invoiceNo: string | null;
  supplier: string | null;
}

export function findProductLots(productId: string): Promise<ProductLot[]> {
  return baseUrl
    .get<{ data: ProductLot[] }>(`${INVENTORY}/product/findProductLots`, {
      headers: { productid: productId },
    })
    .then((response) => response.data.data);
}

export function searchProductBrands(keyword: string): Promise<unknown[]> {
  return baseUrl
    .get<{ data: unknown[] }>(`${INVENTORY}/product/searchAllBrand`, {
      headers: { keyword },
    })
    .then((response) => response.data.data);
}

export function searchProductCategories(keyword: string): Promise<unknown[]> {
  return baseUrl
    .get<{ data: unknown[] }>(`${INVENTORY}/product/searchAllCategory`, {
      headers: { keyword },
    })
    .then((response) => response.data.data);
}

export function searchProductSubCategories(
  keyword: string,
  categoryId?: string
): Promise<unknown[]> {
  const headers: Record<string, string> = { keyword };
  if (categoryId) {
    headers.categoryid = categoryId;
  }
  return baseUrl
    .get<{ data: unknown[] }>(`${INVENTORY}/product/searchAllSubCategory`, {
      headers,
    })
    .then((response) => response.data.data);
}

export function findProductById(productId: string): Promise<unknown> {
  return baseUrl
    .get<{ data: unknown }>(`${INVENTORY}/product/findProductById`, {
      headers: { productid: productId },
    })
    .then((response) => response.data.data);
}

export function createProduct(data: unknown): Promise<void> {
  return baseUrl
    .post(`${INVENTORY}/product/createProduct`, data)
    .then(() => undefined);
}

export function updateProductById(
  productId: string,
  data: unknown
): Promise<void> {
  return baseUrl
    .patch(`${INVENTORY}/product/updateProductById`, data, {
      headers: { productid: productId },
    })
    .then(() => undefined);
}

export function updateProductPriceById(
  productId: string,
  data: unknown
): Promise<void> {
  return baseUrl
    .patch(`${INVENTORY}/product/updateProductPriceById`, data, {
      headers: { productid: productId },
    })
    .then(() => undefined);
}

// --- Purchase orders ---

export function createPurchaseOrder(data: unknown): Promise<unknown> {
  return baseUrl
    .post<{ data: unknown }>(`${INVENTORY}/purchaseOrder/createPurchaseOrder`, data)
    .then((response) => response.data.data);
}

export function findAllPurchaseOrder(
  status?: string,
  supplierId?: string
): Promise<unknown[]> {
  return baseUrl
    .get<{ data: unknown[] }>(`${INVENTORY}/purchaseOrder/findAllPurchaseOrder`, {
      headers: { status: status || "", supplierid: supplierId || "" },
    })
    .then((response) => response.data.data);
}

export function findPurchaseOrderById(orderId: string): Promise<unknown> {
  return baseUrl
    .get<{ data: unknown }>(`${INVENTORY}/purchaseOrder/findPurchaseOrderById`, {
      headers: { purchaseorderid: orderId },
    })
    .then((response) => response.data.data);
}

export function updatePurchaseOrder(
  orderId: string,
  data: unknown
): Promise<void> {
  return baseUrl
    .patch(`${INVENTORY}/purchaseOrder/updatePurchaseOrder`, data, {
      headers: { purchaseorderid: orderId },
    })
    .then(() => undefined);
}

export function updatePurchaseOrderStatus(
  orderId: string,
  status: string
): Promise<void> {
  return baseUrl
    .patch(
      `${INVENTORY}/purchaseOrder/updatePurchaseOrderStatus`,
      { status },
      { headers: { purchaseorderid: orderId } }
    )
    .then(() => undefined);
}

/** Open orders for the supplier chosen on a purchase. */
export function searchOpenPurchaseOrders(
  supplierId: string,
  keyword = ""
): Promise<unknown[]> {
  return baseUrl
    .get<{ data: unknown[] }>(
      `${INVENTORY}/purchaseOrder/searchOpenPurchaseOrders`,
      { headers: { supplierid: supplierId, keyword } }
    )
    .then((response) => response.data.data);
}

// --- Supplier ---


export function findAllSupplier(): Promise<unknown[]> {
  return baseUrl
    .get<{ data: unknown[] }>(`${INVENTORY}/supplier/findAllSupplier`)
    .then((response) => response.data.data);
}

export function findSupplierById(supplierId: string): Promise<unknown> {
  return baseUrl
    .get<{ data: unknown }>(`${INVENTORY}/supplier/findSupplierById`, {
      headers: { supplierid: supplierId },
    })
    .then((response) => response.data.data);
}

export function createSupplier(data: unknown): Promise<void> {
  return baseUrl
    .post(`${INVENTORY}/supplier/createSupplier`, data)
    .then(() => undefined);
}

export function updateSupplierById(
  supplierId: string,
  data: unknown
): Promise<void> {
  return baseUrl
    .patch(`${INVENTORY}/supplier/updateSupplierById`, data, {
      headers: { supplierid: supplierId },
    })
    .then(() => undefined);
}

// --- Purchase ---

export function findAllPurchase(): Promise<unknown[]> {
  return baseUrl
    .get<{ data: unknown[] }>(`${INVENTORY}/purchase/findAllPurchase`)
    .then((response) => response.data.data);
}

export function searchPurchaseSuppliers(keyword: string): Promise<unknown[]> {
  return baseUrl
    .get<{ data: unknown[] }>(`${INVENTORY}/purchase/searchAllSupplier`, {
      headers: { keyword },
    })
    .then((response) => response.data.data);
}

export function findPurchaseById(purchaseId: string): Promise<unknown> {
  return baseUrl
    .get<{ data: unknown }>(`${INVENTORY}/purchase/findPurchaseById`, {
      headers: { purchaseid: purchaseId },
    })
    .then((response) => response.data.data);
}

export function searchPurchaseProducts(keyword: string): Promise<unknown[]> {
  return baseUrl
    .get<{ data: unknown[] }>(`${INVENTORY}/purchase/searchAllProduct`, {
      headers: { keyword },
    })
    .then((response) => response.data.data);
}

export function createPurchase(data: unknown): Promise<void> {
  return baseUrl
    .post(`${INVENTORY}/purchase/createPurchase`, data)
    .then(() => undefined);
}

export function updatePurchase(
  purchaseId: string,
  data: unknown
): Promise<void> {
  return baseUrl
    .patch(`${INVENTORY}/purchase/updatePurchase`, data, {
      headers: { purchaseid: purchaseId },
    })
    .then(() => undefined);
}

// --- Release ---

export function findAllRelease(type: string): Promise<unknown[]> {
  return baseUrl
    .get<{ data: unknown[] }>(`${INVENTORY}/release/findAllRelease`, {
      headers: { type },
    })
    .then((response) => response.data.data);
}

export function searchReleaseEmployees(keyword: string): Promise<unknown[]> {
  return baseUrl
    .get<{ data: unknown[] }>(`${INVENTORY}/release/searchAllEmployees`, {
      headers: { keyword },
    })
    .then((response) => response.data.data);
}

export async function searchReleasePatients(
  keyword: string
): Promise<unknown[]> {
  const response = await baseUrl.get<{ data: unknown[] }>(
    `${INVENTORY}/release/searchAllPatients`,
    { headers: { keyword } }
  );
  return response.data.data;
}

/** `releaseType` limits the list to products whose category allows that release. */
export function searchProductForRelease(keyword: string, releaseType?: string): Promise<unknown[]> {
  const headers: Record<string, string> = { keyword };
  if (releaseType) headers.releasetype = releaseType;
  return baseUrl
    .get<{ data: unknown[] }>(`${INVENTORY}/release/searchProductForRelease`, {
      headers,
    })
    .then((response) => response.data.data);
}

/** Products for a treatment stage, narrowed by category / brand (both optional). */
/**
 * Products for a treatment stage. With `scope` the backend applies the
 * stage's catalog category link (and ignores `categoryId` when there is one).
 */
export function searchProductForStage(params: {
  keyword?: string;
  categoryId?: string;
  brandId?: string;
  scope?: { treatmentId?: string; typeId?: string; subtypeId?: string; stage?: string };
}): Promise<unknown[]> {
  const headers: Record<string, string> = { keyword: params.keyword || "" };
  if (params.categoryId) headers.categoryid = params.categoryId;
  if (params.brandId) headers.brandid = params.brandId;
  const scope = params.scope;
  if (scope?.treatmentId && scope.stage) {
    headers.treatmentid = scope.treatmentId;
    if (scope.typeId) headers.typeid = scope.typeId;
    if (scope.subtypeId) headers.subtypeid = scope.subtypeId;
    // headers are ASCII only
    headers.stage = encodeURIComponent(scope.stage);
  }
  return baseUrl
    .get<{ data: unknown[] }>(`${INVENTORY}/release/searchProductForStage`, { headers })
    .then((response) => response.data.data);
}

export function employeeProductRelease(data: unknown): Promise<void> {
  return baseUrl
    .post(`${INVENTORY}/release/employeeProductRelease`, data)
    .then(() => undefined);
}

export function clinicProductRelease(data: unknown): Promise<void> {
  return baseUrl
    .post(`${INVENTORY}/release/clinicProductRelease`, data)
    .then(() => undefined);
}

export function patientsProductRelease(data: unknown): Promise<void> {
  return baseUrl
    .post(`${INVENTORY}/release/patientsProductRelease`, data)
    .then(() => undefined);
}

export function unsettledDispensesByPatient(
  patientId: string
): Promise<{ groups: unknown[]; outstandingTotal: number }> {
  return baseUrl
    .get<{ data: { groups: unknown[]; outstandingTotal: number } }>(
      `${INVENTORY}/release/unsettledDispensesByPatient`,
      { headers: { patientid: patientId } }
    )
    .then((response) => response.data.data);
}

// --- Prescription dose link (product side) ---

const PRESCRIPTION = "client/prescription/prescription";

export type LinkableDose = {
  _id: string;
  value: string;
  products: { _id: string; productName: string; scale?: string }[];
  /** Already holds products in another unit, so this product can't join. */
  blocked: boolean;
};

export type LinkableDrug = {
  _id: string;
  drugs: string;
  /** Strength unit, e.g. mg. */
  type: string;
  /** Stock unit; empty on drugs that have never been linked. */
  form: string;
  dose: LinkableDose[];
};

/** Drugs whose form matches `scale` (or that have none yet). */
export function searchDrugsForProductLink(
  keyword: string,
  scale: string
): Promise<LinkableDrug[]> {
  return baseUrl
    .get<{ data: LinkableDrug[] }>(`${PRESCRIPTION}/searchDrugsForProductLink`, {
      headers: { keyword, scale },
    })
    .then((response) => response.data.data || []);
}

export function addDoseToDrug(
  prescriptionId: string,
  value: string
): Promise<{ _id: string; value: string }> {
  return baseUrl
    .post<{ data: { dose: { _id: string; value: string } } }>(
      `${PRESCRIPTION}/addDoseToDrug`,
      { value },
      { headers: { prescriptionid: prescriptionId } }
    )
    .then((response) => response.data.data.dose);
}

export function createDrug(data: {
  drugs: string;
  type: string;
  form: string;
  doses: string[];
}): Promise<{ _id: string; dose: { _id: string; value: string }[] }> {
  return baseUrl
    .post<{ data: { _id: string; dose: { _id: string; value: string }[] } }>(
      `${PRESCRIPTION}/createPrescription`,
      data
    )
    .then((response) => response.data.data);
}

export function linkProductToDose(
  prescriptionId: string,
  productId: string,
  doseId: string
): Promise<void> {
  return baseUrl
    .patch(
      `${PRESCRIPTION}/linkProductToDose`,
      { productId, doseId },
      { headers: { prescriptionid: prescriptionId } }
    )
    .then(() => undefined);
}

export function unlinkProductFromDose(productId: string): Promise<void> {
  return baseUrl
    .patch(`${PRESCRIPTION}/unlinkProductFromDose`, { productId })
    .then(() => undefined);
}
